<template>
  <div class="lawyer-page">
    <div class="main-content">
      <!-- 第一部分：英雄区域 -->
      <section class="hero-section">
        <div class="container">
          <AppHeader />
          
          <div class="hero-content">
            <h1 class="hero-title animate">闻讼认证律师</h1>
            <h2 class="hero-subtitle animate">优秀的团队值得信赖</h2>
          </div>
        </div>
      </section>
      
      <!-- 第二部分：律师团队 -->
      <section class="lawyer-list-section" ref="lawyerListSection">
        <div class="container">
          <h2 class="section-title animate-scroll">闻讼认证律师</h2>
          
          <div class="lawyer-list animate-scroll">
            <div class="lawyer-row" v-for="(row, rowIndex) in lawyerRows" :key="rowIndex">
              <router-link
                v-for="(lawyer, index) in row"
                :key="lawyer.id"
                :to="{ name: 'LawyerDetail', params: { id: lawyer.id } }"
                class="lawyer-card"
                :style="{ 'animation-delay': `${0.1 * (rowIndex * 2 + index + 1)}s` }"
              >
                <div class="lawyer-photo">
                  <img :src="lawyer.avatar || require('@/assets/image/group1@2x.png')" :alt="lawyer.name">
                </div>
                <div class="lawyer-info">
                  <h3 class="lawyer-name-cn">{{ lawyer.name }}</h3>
                  <h4 class="lawyer-name-en">{{ lawyer.nameEn }}</h4>
                  <p class="lawyer-position">{{ lawyer.position }}</p>
                  <div class="lawyer-contact" v-if="lawyer.email">
                    <span class="contact-label">邮箱：</span>
                    <span class="contact-email">{{ lawyer.email }}</span>
                  </div>
                  <div class="lawyer-contact" v-if="lawyer.area && lawyer.area.length > 0">
                    <span class="contact-label">工作地点：</span>
                    <span class="contact-email" v-for="(item,index) in lawyer.area" :key="index">{{ item }}{{ index < lawyer.area.length - 1 ? '、' : '' }}</span>
                  </div>
                  <div class="lawyer-intro" v-if="lawyer.intro">
                    <p>{{ lawyer.intro }}</p>
                  </div>
                  <div class="lawyer-more">
                    <span>查看更多 ></span>
                  </div>
                </div>
              </router-link>
            </div>
          </div>
          <!-- 分页 -->
          <div v-show="!loading && lawyers.length > 0 && total > params.limit" class="pagination animate-scroll" style="animation-delay: 0.7s;">
            <a href="javascript:void(0)" class="page-link" @click="prevPage" :class="{ disabled: params.page <= 1 }">&lt;</a>
            <template v-for="p in displayPageNumbers" :key="p">
              <a v-if="p === '...'" class="page-link">...</a>
              <a v-else href="javascript:void(0)" 
                 class="page-link" 
                 :class="{ active: params.page === p }" 
                 @click="changePage(p)">{{p}}</a>
            </template>
            <a href="javascript:void(0)" class="page-link" @click="nextPage" :class="{ disabled: params.page >= totalPages }">&gt;</a>
          </div>
        </div>
      </section>
    </div>
    <div class="footer-wrapper">
      <AppFooter />
    </div>
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue';
import AppHeader from '@/components/AppHeader.vue';
import { getLawyerList } from '@/api/index';
import pinyin from 'pinyin';

export default {
  name: 'LawyerPage',
  components: {
    AppFooter,
    AppHeader
  },
  data() {
    return {
      observers: [],
      lawyers: [],
      loading: false,
      params: {
        page: 1,
        limit: 10 // 每页显示8个律师
      },
      total: 0 // 律师总数
    }
  },
  mounted() {
    this.initScrollAnimations();
    
    // 首屏动画自动播放
    const animateElements = document.querySelectorAll('.animate');
    animateElements.forEach((el, index) => {
      setTimeout(() => {
        el.classList.add('visible');
      }, 300 * index);
    });
    // 获取URL中的页码参数
    const pageParam = parseInt(this.$route.query.page);
    if (pageParam && !isNaN(pageParam) && pageParam > 0) {
      this.params.page = pageParam;
    }
    this.fetchLawyerList();
  },
  beforeUnmount() {
    // 清除所有观察者
    this.observers.forEach(observer => observer.disconnect());
  },
  methods: {
    // 将中文姓名转换为拼音
    convertToPinyin(chineseName) {
      if (!chineseName) return '';
      
      // 使用pinyin库转换，不带声调
      const pinyinArray = pinyin(chineseName, {
        style: pinyin.STYLE_NORMAL, // 不带声调
        heteronym: false, // 不显示多音字的多个读音
        segment: true // 启用分词
      });
      
      // 将二维数组扁平化
      const pinyinList = pinyinArray.map(item => item[0]);
      
      if (pinyinList.length === 0) return '';
      
      // 第一个字（姓氏）全大写
      const surname = pinyinList[0].toUpperCase();
      
      // 名字部分：第一个字首字母大写，其余字全部小写
      const givenNameParts = pinyinList.slice(1);
      let givenName = '';
      if (givenNameParts.length > 0) {
        // 第一个字首字母大写，其余小写
        const firstGivenName = givenNameParts[0].charAt(0).toUpperCase() + givenNameParts[0].slice(1).toLowerCase();
        // 其余字全部小写
        const restGivenNames = givenNameParts.slice(1).map(name => name.toLowerCase());
        givenName = firstGivenName + restGivenNames.join('');
      }
      
      // 姓氏和名字之间用空格连接
      return givenName ? `${surname} ${givenName}` : surname;
    },
    async fetchLawyerList() {
      this.loading = true;
      try {
        const res = await getLawyerList(this.params);
        if (res && res.data) {
          // 兼容分页结构
          let list = [];
          let totalCount = 0;
          if (res.data && res.data.data && Array.isArray(res.data.data)) {
            list = res.data.data;
            totalCount = res.data.total || 0;
          } else if (res.data && Array.isArray(res.data)) {
            list = res.data;
            totalCount = res.total || list.length || 0;
          } else {
            list = [];
            totalCount = 0;
          }
          this.lawyers = list.map(item => ({
            id: item.id,
            avatar: item.u_image,
            name: item.u_name,
            nameEn: this.convertToPinyin(item.u_name), // 使用拼音转换函数
            position: item.u_position,
            email: item.email,
            intro: item.u_introduction,
            area: item.area
          }));
          this.total = totalCount;
          this.$nextTick(() => {
            // nothing, 由IntersectionObserver控制动画
          });
        }
      } catch (e) {
        // 可加错误提示
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (this.params.page === page) return;
      this.params.page = page;
      this.fetchLawyerList();
      this.$refs.lawyerListSection.scrollIntoView({ behavior: 'smooth' });
    },
    prevPage() {
      if (this.params.page > 1) {
        this.changePage(this.params.page - 1);
      }
    },
    nextPage() {
      if (this.params.page < this.totalPages) {
        this.changePage(this.params.page + 1);
      }
    },
    initScrollAnimations() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      const section = this.$refs.lawyerListSection;
      if (section) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const animatedElements = entry.target.querySelectorAll('.animate-scroll');
              animatedElements.forEach((el, idx) => {
                setTimeout(() => {
                  el.classList.add('visible');
                }, idx * 120);
              });
            }
          });
        }, options);
        observer.observe(section);
        this.observers.push(observer);
      }
    }
  },
  computed: {
    lawyerRows() {
      // 每行2个律师卡片
      const rows = [];
      for (let i = 0; i < this.lawyers.length; i += 2) {
        rows.push(this.lawyers.slice(i, i + 2));
      }
      return rows;
    },
    totalPages() {
      return Math.ceil(this.total / this.params.limit) || 1;
    },
    displayPageNumbers() {
      const current = this.params.page;
      const total = this.totalPages;
      const delta = 1;
      let pages = [];
      pages.push(1);
      if (current - delta > 2) {
        pages.push('...');
      }
      let start = Math.max(2, current - delta);
      let end = Math.min(total - 1, current + delta);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (current + delta < total - 1) {
        pages.push('...');
      }
      if (total > 1) {
        pages.push(total);
      }
      return pages;
    }
  }
}
</script>

<style scoped>
.lawyer-page {
  margin: 0;
  padding: 0;
  font-family: Arial, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.footer-wrapper {
  flex-shrink: 0;
}

/* 入场动画基础样式 */
.animate, .animate-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 1s ease, transform 1s ease;
}

.animate.visible, .animate-scroll.visible {
  opacity: 1;
  transform: translateY(0);
}

/* 英雄区域样式 */
.hero-section {
  width: 1920px;
  height: 600px;
  background-image: url('~@/assets/image/zytd.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.container {
  width: 1200px;
  margin: 0 auto;
  position: relative;
}

.logo-area {
  padding-top: 20px;
}

.logo {
  width: 109px;
  height: 40px;
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 322px;
}

.hero-title {
  font-weight: 600;
  font-size: 48px;
  color: #FFFFFF;
  margin: 0;
}

.hero-subtitle {
  font-weight: 400;
  font-size: 24px;
  color: #FFFFFF;
  margin-top: 20px;
}

/* 律师列表部分样式 */
.lawyer-list-section {
  width: 1920px;
  background: #FFFFFF;
  padding: 60px 0;
  position: relative;
  overflow: hidden;
}

.section-title {
  font-weight: 600;
  font-size: 36px;
  color: #333333;
  text-align: left;
  margin-bottom: 40px;
}

.lawyer-list {
  margin-top: 40px;
}

.lawyer-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
}

.lawyer-card {
  width: 580px;
  display: flex;
  flex-direction: row;
  background-color: #FFFFFF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  text-decoration: none;
}

.lawyer-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.lawyer-photo {
  width: 200px;
  /* height: 280px; */
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  background-color: #8E9296;
}

.lawyer-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lawyer-info {
  padding: 24px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.lawyer-name-cn {
  font-weight: 600;
  font-size: 24px;
  color: #333333;
  margin: 0 0 6px 0;
  line-height: 1.2;
}

.lawyer-name-en {
  font-weight: 400;
  font-size: 16px;
  color: #666666;
  margin: 0 0 12px 0;
  letter-spacing: 0.5px;
}

.lawyer-position {
  font-size: 14px;
  color: #666666;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.lawyer-contact {
  font-size: 14px;
  color: #333333;
  margin-bottom: 16px;
  line-height: 1.5;
}

.contact-label {
  color: #666666;
}

.contact-email {
  color: #333333;
  word-break: break-all;
}

.lawyer-intro {
  font-size: 13px;
  color: #666666;
  line-height: 1.5;
  margin: 12px 0;
}

.lawyer-intro p {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lawyer-more {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.lawyer-more span {
  font-size: 14px;
  color: #666666;
  cursor: pointer;
  transition: color 0.3s ease;
}

.lawyer-card:hover .lawyer-more span {
  color: #3578E5;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 60px;
}

.page-link {
  width: 36px;
  height: 36px;
  border: 1px solid #DDDDDD;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 5px;
  color: #333333;
  text-decoration: none;
  font-size: 16px;
  transition: all 0.3s ease;
}

.page-link.active {
  background-color: #3578E5;
  color: #FFFFFF;
  border-color: #3578E5;
}

.page-link:hover:not(.active) {
  border-color: #3578E5;
  color: #3578E5;
}
</style> 